import { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ── Icons ──
const CloseIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M12.32 1.07a1 1 0 011.36.19l4.55 5.9a1 1 0 01-.19 1.39l-.01.01-1.7 1.31a2.5 2.5 0 01-2.13.41l-2.96 3.83a5 5 0 01.57 3.26L11.81 17a1 1 0 01-1.57.21L7.2 14.17l-3.72 4.8a.75.75 0 01-1.18-.92l3.72-4.8-2.73-2.11a1 1 0 01.21-1.72l-.01.01.02-.01 2.64-1.02a5 5 0 013.2-.49l3-3.87a2.5 2.5 0 01.52-2.61l.01-.01z" />
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M3 3h6l8 8-6 6-8-8V3zM7 7h.01" />
  </svg>
);
const TextIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M3 5h14M3 10h10M3 15h7" />
  </svg>
);
const TitleIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 6h12M4 10h8M4 14h5" />
    <rect x="2" y="3" width="16" height="14" rx="2" />
  </svg>
);
const HistoryIcon = () => (
  <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="10" cy="10" r="8" />
    <path d="M10 6v4l2.5 2.5M3 3l3 3" />
  </svg>
);

// ── Categories ──
const CATEGORIES = [
  { value: "General",     label: "General",     emoji: "📋", bg: "#EAF3DE", text: "#3B6D11" },
  { value: "Tender",      label: "Tender",      emoji: "📑", bg: "#ede9fe", text: "#5b21b6" },
  { value: "Recruitment", label: "Recruitment", emoji: "👥", bg: "#dbeafe", text: "#1e40af" },
  { value: "Policy",      label: "Policy",      emoji: "📜", bg: "#fce7f3", text: "#9d174d" },
  { value: "Holiday",     label: "Holiday",     emoji: "🎉", bg: "#fef9c3", text: "#854d0e" },
  { value: "Urgent",      label: "Urgent",      emoji: "🚨", bg: "#fee2e2", text: "#991b1b" },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

// ── Diff helper — highlight what changed ──
function hasChanges(original, current) {
  return (
    original.title !== current.title ||
    original.content !== current.content ||
    original.category !== current.category ||
    original.isPinned !== current.isPinned
  );
}

function getChangedFields(original, current) {
  const changed = [];
  if (original.title !== current.title) changed.push("Title");
  if (original.content !== current.content) changed.push("Content");
  if (original.category !== current.category) changed.push("Category");
  if (original.isPinned !== current.isPinned)
    changed.push(current.isPinned ? "Pinned" : "Unpinned");
  return changed;
}

// ── Live preview ──
function NoticePreview({ form }) {
  const cat = CAT_MAP[form.category];
  const now = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        border: form.isPinned ? "1.5px solid #4ade80" : "1.5px solid #e8f0e0",
        boxShadow: form.isPinned ? "0 4px 20px rgba(74,222,128,0.12)" : "0 2px 10px rgba(0,0,0,0.04)",
        background: "white",
      }}>
      {form.isPinned && (
        <div className="h-1 w-full" style={{ background: "linear-gradient(to right, #6ee85a, #3dcc40)" }} />
      )}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {cat && (
              <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
                style={{ background: cat.bg, color: cat.text }}>
                {cat.emoji} {cat.label}
              </span>
            )}
            {form.isPinned && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "#fef9c3", color: "#92400e" }}>📌 Pinned</span>
            )}
          </div>
          <span className="text-xs text-gray-400">{now}</span>
        </div>
        <h3 className="font-black text-gray-900 leading-snug mb-2"
          style={{ fontSize: "15px", minHeight: "20px", color: form.title ? "#111" : "#d1d5db" }}>
          {form.title || "Notice title will appear here..."}
        </h3>
        <p className="text-gray-500 text-sm leading-relaxed line-clamp-3"
          style={{ minHeight: "60px", color: form.content ? "#6b7280" : "#d1d5db" }}>
          {form.content || "Notice content preview..."}
        </p>
        <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: "0.5px solid #f0f5ee" }}>
          <span className="text-xs text-green-600 font-semibold">Read more →</span>
          <span className="text-xs text-gray-400">AG Services</span>
        </div>
      </div>
    </div>
  );
}

// ── Char counter ──
function CharCount({ value, max }) {
  const pct = value.length / max;
  const color = pct > 0.9 ? "#ef4444" : pct > 0.75 ? "#f59e0b" : "#4ade80";
  return <span className="text-xs font-medium" style={{ color }}>{value.length}/{max}</span>;
}

// ── Change badge ──
function ChangeBadge({ changes }) {
  if (!changes.length) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {changes.map(c => (
        <span key={c} className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: "#fef9c3", color: "#92400e" }}>
          ✎ {c}
        </span>
      ))}
    </div>
  );
}

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "color",
  "background",
  "list",
  "bullet",
  "link",
];

// ── Main Modal ──
export default function EditNoticeModal({ isOpen, onClose, onSuccess, notice }) {
  // "notice" prop shape: { _id, title, content, category, isPinned, createdAt, updatedAt }

  const [form, setForm] = useState({ title: "", content: "", category: "", isPinned: false });
  const [original, setOriginal] = useState({ title: "", content: "", category: "", isPinned: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const titleRef = useRef();

  // Populate form when notice changes
  useEffect(() => {
    if (isOpen && notice) {
      const initial = {
        title: notice.title || "",
        content: notice.content || "",
        category: notice.category || "",
        isPinned: notice.isPinned || false,
      };
      setForm(initial);
      setOriginal(initial);
      setErrors({});
      setSubmitted(false);
      setShowPreview(false);
      setDiscardPrompt(false);
      setTimeout(() => titleRef.current?.focus(), 120);
    }
  }, [isOpen, notice]);

  // ESC key — prompt if unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && isOpen) {
        if (hasChanges(original, form)) setDiscardPrompt(true);
        else onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, form, original, onClose]);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";
    else if (form.title.trim().length < 5) e.title = "Title must be at least 5 characters";
    else if (form.title.length > 200) e.title = "Title cannot exceed 200 characters";
    if (!form.content.trim()) e.content = "Content is required";
    else if (form.content.trim().length < 10) e.content = "Content must be at least 10 characters";
    return e;
  };

  const handleReset = () => {
    setForm({ ...original });
    setErrors({});
  };

  const handleClose = () => {
    if (loading) return;
    if (hasChanges(original, form)) { setDiscardPrompt(true); return; }
    onClose();
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    if (!hasChanges(original, form)) { onClose(); return; }

    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/notice/edit/${notice._id}`,form);
      setSubmitted(true);
      onSuccess?.();
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || "Failed to update notice. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !notice) return null;

  const changedFields = getChangedFields(original, form);
  const isDirty = changedFields.length > 0;
  const cat = CAT_MAP[form.category];

  const inputStyle = (field) => ({
    border: `1.5px solid ${errors[field] ? "#fca5a5" : form[field] ? "#4ade80" : "#e5e7eb"}`,
    background: errors[field] ? "#fff5f5" : form[field] ? "#f0fdf4" : "#fafafa",
    fontFamily: "inherit",
    color: "#374151",
  });

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
        onClick={handleClose}
      >
        <div
          className="w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto no-scrollbar rounded-t-3xl sm:rounded-2xl bg-white"
          style={{ fontFamily: "'Barlow', 'Montserrat', sans-serif" }}
          onClick={e => e.stopPropagation()}
        >

          {/* ── Header ── */}
          <div
            className="sticky top-0 z-10 px-6 py-5"
            style={{ background: "linear-gradient(135deg, #1a2e6e 0%, #2a3f9a 100%)", borderRadius: "inherit inherit 0 0" }}
          >
            <div className="absolute inset-0 pointer-events-none rounded-t-2xl"
              style={{ backgroundImage: "radial-gradient(circle, rgba(100,130,255,0.08) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #6080ff, #4060e0)", boxShadow: "0 4px 12px rgba(100,130,255,0.4)" }}>
                  <EditIcon />
                </div>
                <div>
                  <h2 className="text-white font-black text-base leading-none">Edit Notice</h2>
                  <p className="text-blue-300 text-xs mt-0.5 flex items-center gap-1">
                    <HistoryIcon />
                    {notice.updatedAt
                      ? `Last updated ${new Date(notice.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                      : `Created ${new Date(notice.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Preview toggle */}
                <button
                  onClick={() => setShowPreview(p => !p)}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                  style={{
                    background: showPreview ? "rgba(100,130,255,0.25)" : "rgba(255,255,255,0.1)",
                    color: showPreview ? "#a5b4fc" : "rgba(255,255,255,0.7)",
                    border: showPreview ? "1px solid rgba(100,130,255,0.4)" : "1px solid rgba(255,255,255,0.15)",
                  }}>
                  {showPreview ? "✓ Preview" : "Preview"}
                </button>
                {!loading && (
                  <button onClick={handleClose} className="text-white/60 hover:text-white transition-colors p-1">
                    <CloseIcon />
                  </button>
                )}
              </div>
            </div>

            {/* Change indicator bar */}
            {isDirty && !submitted && (
              <div className="relative z-10 mt-3 flex items-center justify-between flex-wrap gap-2">
                <ChangeBadge changes={changedFields} />
                <button
                  onClick={handleReset}
                  className="text-xs font-semibold px-3 py-1 rounded-lg transition-all hover:bg-white/10"
                  style={{ color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  ↺ Reset changes
                </button>
              </div>
            )}
          </div>

          {submitted ? (
            /* ── Success ── */
            <div className="flex flex-col items-center text-center px-8 py-14 gap-5">
              <div className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #6080ff, #4060e0)", boxShadow: "0 8px 28px rgba(100,130,255,0.4)" }}>
                <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M6 16l8 8L26 8" />
                </svg>
              </div>
              <div>
                <h3 className="font-black text-gray-900 text-xl mb-2">Notice Updated!</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
                  <strong className="text-gray-800">"{form.title}"</strong> has been updated successfully.
                  {changedFields.length > 0 && (
                    <span className="block mt-1 text-xs text-gray-400">Changed: {changedFields.join(", ")}</span>
                  )}
                </p>
              </div>
              <button
                onClick={onClose}
                className="px-8 py-3 rounded-xl text-sm font-black transition-all hover:opacity-90 mt-2"
                style={{ background: "linear-gradient(135deg, #6080ff, #4060e0)", color: "white" }}>
                Done
              </button>
            </div>
          ) : (
            <div className={`grid gap-0 ${showPreview ? "sm:grid-cols-2" : "grid-cols-1"}`}>

              {/* ── Form ── */}
              <div className="px-6 py-6 flex flex-col gap-5">

                {/* Original notice info chip */}
                <div className="flex items-center gap-2 p-3 rounded-xl flex-wrap"
                  style={{ background: "#f8f9ff", border: "1px solid #e0e7ff" }}>
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="#6080ff">
                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 3a.5.5 0 01.5.5v3a.5.5 0 01-1 0v-3A.5.5 0 018 4zm0 6a.75.75 0 110 1.5.75.75 0 010-1.5z"/>
                  </svg>
                  <span className="text-xs text-indigo-600 font-medium">
                    Editing notice ID: <code className="font-mono bg-indigo-50 px-1.5 py-0.5 rounded text-xs">{notice._id?.slice(-8)}</code>
                  </span>
                  {notice.category && (
                    <>
                      <span className="text-indigo-200 text-xs">·</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: CAT_MAP[notice.category]?.bg || "#f3f4f6", color: CAT_MAP[notice.category]?.text || "#374151" }}>
                        {CAT_MAP[notice.category]?.emoji} {notice.category}
                      </span>
                    </>
                  )}
                </div>

                {/* Title */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="flex items-center gap-1.5 text-xs font-black text-gray-700 uppercase tracking-wider">
                      <span style={{ color: "#6080ff" }}><TitleIcon /></span>
                      Title <span className="text-red-400">*</span>
                    </label>
                    <CharCount value={form.title} max={200} />
                  </div>
                  <div className="relative">
                    <input
                      ref={titleRef}
                      name="title"
                      value={form.title}
                      onChange={handle}
                      placeholder="Notice title..."
                      maxLength={200}
                      className="w-full text-sm px-4 py-3 rounded-xl outline-none transition-all"
                      style={inputStyle("title")}
                      onFocus={e => { if (!errors.title) e.target.style.borderColor = "#6080ff"; }}
                      onBlur={e => { if (!errors.title && !form.title) e.target.style.borderColor = "#e5e7eb"; else if (!errors.title) e.target.style.borderColor = "#4ade80"; }}
                    />
                    {/* Changed indicator */}
                    {form.title !== original.title && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold px-1.5 py-0.5 rounded"
                        style={{ background: "#fef9c3", color: "#92400e" }}>edited</span>
                    )}
                  </div>
                  {errors.title && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">⚠ {errors.title}</p>}
                </div>

                {/* Category */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-black text-gray-700 uppercase tracking-wider mb-2">
                    <span style={{ color: "#6080ff" }}><TagIcon /></span>
                    Category
                    {form.category !== original.category && (
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded ml-1"
                        style={{ background: "#fef9c3", color: "#92400e" }}>edited</span>
                    )}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map(c => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, category: f.category === c.value ? "" : c.value }))}
                        className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition-all"
                        style={{
                          background: form.category === c.value ? c.bg : "#fafafa",
                          color: form.category === c.value ? c.text : "#6b7280",
                          border: `1.5px solid ${form.category === c.value ? c.text + "40" : original.category === c.value ? "#fbbf24" : "#e5e7eb"}`,
                          transform: form.category === c.value ? "scale(1.02)" : "scale(1)",
                        }}>
                        {c.emoji} {c.label}
                        {form.category === c.value && (
                          <span className="ml-auto">
                            <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke={c.text} strokeWidth="1.8" strokeLinecap="round">
                              <path d="M2 6l3 3 5-6" />
                            </svg>
                          </span>
                        )}
                        {/* Original marker */}
                        {original.category === c.value && form.category !== c.value && (
                          <span className="ml-auto text-xs" style={{ color: "#fbbf24" }} title="Original value">●</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">● indicates original value</p>
                </div>

                {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                    <label className="flex items-center gap-1.5 text-xs font-black text-gray-700 uppercase tracking-wider">
                    <span style={{ color: "#6080ff" }}><TextIcon /></span>
                    Content <span className="text-red-400">*</span>

                    {form.content !== original.content && (
                        <span
                        className="text-xs font-bold px-1.5 py-0.5 rounded ml-1"
                        style={{ background: "#fef9c3", color: "#92400e" }}
                        >
                        edited
                        </span>
                    )}
                    </label>

                    {/* Optional: character count (strip HTML) */}
                    <CharCount
                    value={form.content.replace(/<[^>]+>/g, "")}
                    max={2000}
                    />
                </div>

                {/* React Quill Editor */}
                <div
                    className={`rounded-xl overflow-hidden border transition-all ${
                    errors.content
                        ? "border-red-400"
                        : "border-gray-200 focus-within:border-[#6080ff]"
                    }`}
                >
                    <ReactQuill
                    theme="snow"
                    value={form.content}
                    onChange={(value) =>
                        handle({ target: { name: "content", value } })
                    }
                    modules={modules}
                    formats={formats}
                    placeholder="Write your notice content..."
                    className="bg-white text-sm"
                    />
                </div>

                {/* Error */}
                {errors.content && (
                    <p className="text-red-400 text-xs mt-1.5">
                    ⚠ {errors.content}
                    </p>
                )}
                </div>

                {/* Pin toggle */}
                <div
                  className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all"
                  style={{
                    background: form.isPinned ? "#f0fdf4" : "#fafafa",
                    border: `1.5px solid ${form.isPinned ? "#4ade80" : form.isPinned !== original.isPinned ? "#fbbf24" : "#e5e7eb"}`,
                  }}
                  onClick={() => setForm(f => ({ ...f, isPinned: !f.isPinned }))}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: form.isPinned ? "linear-gradient(135deg, #6ee85a, #3dcc40)" : "#f3f4f6" }}>
                      <span style={{ color: form.isPinned ? "#0d2d0d" : "#9ca3af" }}><PinIcon /></span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold" style={{ color: form.isPinned ? "#1a2e0a" : "#374151" }}>
                          {form.isPinned ? "Pinned" : "Not pinned"}
                        </p>
                        {form.isPinned !== original.isPinned && (
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded"
                            style={{ background: "#fef9c3", color: "#92400e" }}>
                            {original.isPinned ? "was pinned" : "was unpinned"}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Pinned notices appear at the top of the board</p>
                    </div>
                  </div>
                  <div
                    className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
                    style={{ background: form.isPinned ? "linear-gradient(135deg, #6ee85a, #3dcc40)" : "#d1d5db" }}>
                    <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all"
                      style={{ left: form.isPinned ? "22px" : "2px" }} />
                  </div>
                </div>

                {/* API error */}
                {errors.submit && (
                  <div className="flex items-center gap-2 p-3 rounded-xl text-sm"
                    style={{ background: "#fff5f5", border: "1px solid #fca5a5", color: "#dc2626" }}>
                    ⚠ {errors.submit}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={handleClose}
                    disabled={loading}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
                    style={{ borderColor: "#e0e7ff", color: "#6b7280" }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={loading || !isDirty}
                    className="px-5 py-3 rounded-xl text-sm font-semibold border transition-all"
                    style={{
                      borderColor: isDirty ? "#fbbf24" : "#e5e7eb",
                      color: isDirty ? "#92400e" : "#d1d5db",
                      background: isDirty ? "#fef9c3" : "transparent",
                      cursor: isDirty ? "pointer" : "not-allowed",
                    }}>
                    ↺ Reset
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !isDirty || !form.title.trim() || !form.content.trim()}
                    className="flex-1 py-3 rounded-xl text-sm font-black transition-all flex items-center justify-center gap-2"
                    style={{
                      background: !isDirty || !form.title.trim() || !form.content.trim() || loading
                        ? "#e0e7ff"
                        : "linear-gradient(135deg, #6080ff, #4060e0)",
                      color: !isDirty || !form.title.trim() || !form.content.trim() || loading ? "#a5b4fc" : "white",
                      cursor: !isDirty || loading ? "not-allowed" : "pointer",
                      boxShadow: isDirty && !loading ? "0 4px 14px rgba(96,128,255,0.35)" : "none",
                    }}>
                    {loading ? (
                      <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Saving...</>
                    ) : !isDirty ? (
                      "No changes"
                    ) : (
                      <><span>💾</span> Save Changes</>
                    )}
                  </button>
                </div>
              </div>

              {/* ── Preview Panel ── */}
              {showPreview && (
                <div className="px-5 py-6 border-l" style={{ borderColor: "#f0f5ee", background: "#f8faf6" }}>
                  <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4">Live Preview</p>
                  <NoticePreview form={form} />
                  {isDirty && (
                    <div className="mt-3 p-2 rounded-xl text-center"
                      style={{ background: "#fef9c3", border: "1px solid #fde68a" }}>
                      <p className="text-xs font-semibold" style={{ color: "#92400e" }}>
                        ✎ {changedFields.length} unsaved change{changedFields.length > 1 ? "s" : ""}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Discard confirmation ── */}
      {discardPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center"
            style={{ fontFamily: "'Barlow', sans-serif" }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "#fff7ed", border: "2px solid #fed7aa" }}>
              <span className="text-2xl">⚠️</span>
            </div>
            <h3 className="font-black text-gray-900 text-lg mb-2">Discard Changes?</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-5">
              You have <strong>{changedFields.length} unsaved change{changedFields.length > 1 ? "s" : ""}</strong> ({changedFields.join(", ")}). They will be permanently lost.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDiscardPrompt(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
                style={{ borderColor: "#e5e7eb", color: "#374151" }}>
                Keep Editing
              </button>
              <button
                onClick={() => { setDiscardPrompt(false); onClose(); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-black transition-all"
                style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}>
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}